import Check from "@mui/icons-material/Check";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { GridRowParams } from "@mui/x-data-grid";
import { ArrowRightIcon } from "@mui/x-date-pickers";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { gql } from "generated-graphql";
import {
  WasteClassification,
  WasteStreamRegistrationStatus,
  WasteStreamsQuery,
  WasteStreamsQueryVariables,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useOmnisearchInput } from "hooks/useOmnisearchDatagrid/useOmnisearchInput";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useTenant } from "hooks/useTenant";
import { useShowWasteStateRegistration } from "hooks/useWasteStateRegistration";
import { useMemo, useState } from "react";
import { formatAmountInPounds } from "util/formatNumber";

import { profileNumberValueGetter } from "../formatWasteStreamProfileNumber";
import { MergedWasteStreamChip } from "../MergedWasteStreamChip";
import { prettyPrintWasteClassification } from "../prettyPrintWasteClassification";
import { prettyPrintWasteStreamRegistrationStatus } from "../prettyPrintWasteStreamRegistrationStatus";
import { ProvisionalWasteStreamChip } from "../ProvisionalWasteStreamChip";
import { useWasteFacility } from "../useWasteFacility";
import { useWasteFacilityState } from "../WasteFacilityContext";
import { WasteStreamDetailsModal } from "../WasteStreamDetails/WasteStreamDetailsModal";
import { useWasteStreamSelection } from "./useWasteStreamSelection";
import { WasteStreamExportDropdown } from "./WasteStreamExportDropdown";
import { WasteStreamMergeDialog } from "./WasteStreamMerge/WasteStreamMergeDialog";

const WASTE_STREAMS = gql(`
  query WasteStreams(
    $facilityId: ID!,
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
    $startDate: Date
    $endDate: Date
    $view: FacilityWasteView
    $tags: [String!]
    $needsStateRegistration: Boolean
  ) {
    facility(id: $facilityId) {
      state
      wasteStreams(search: $search, page: $page, pageSize: $pageSize, sort: $sort, startDate: $startDate, endDate: $endDate, view: $view, tags: $tags, needsStateRegistration: $needsStateRegistration) {
        items {
          id
          name
          facilityId
          classification
          federalWasteCodes
          stateWasteCodes
          eManifestProfileNumber
          vendorProfileNumber
          formCode
          sourceCode
          managementMethodCodes
          vendorAmount
          eManifestAmount
          effectiveAmount
          vendorAmountAllTime
          eManifestAmountAllTime
          mergeCount
          comment
          willNotReport
          stateRegisteredId
          stateRegistrationStatus
          stateRegisteredAt
          isProvisional
        }
        count
      }
    }
  }
`);

type WasteStreamTileRow = NonNullable<
  NonNullable<WasteStreamsQuery["facility"]>["wasteStreams"]
>["items"][number];

interface WasteStreamsDataGridProps {
  facilityId: string;
}

export const WasteStreamsDataGrid = ({ facilityId }: WasteStreamsDataGridProps) => {
  const theme = useTheme();
  const { isStaff } = useCurrentUser();
  const { tenantId } = useTenant();
  const { data } = useWasteFacility(facilityId);
  const { selectedRows, setSelectedRows, isRowSelectable, canMergeSelected } =
    useWasteStreamSelection<WasteStreamTileRow>();

  const { selectedId, pushIdToUrl, popIdFromUrl } = useSelectedIdInUrl("wasteStreamId", {
    prefixSegment: "wasteStream",
  });

  const handleRowClick = (params: GridRowParams<WasteStreamTileRow>) => {
    pushIdToUrl(params.row.id);
  };

  const {
    wasteFacilityState: { deferredFilters },
  } = useWasteFacilityState();
  const { omnisearch } = useOmnisearchInput({ isURLDriven: true });
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);
  const [, setQueryData] = useState<WasteStreamsQuery | undefined>(undefined);

  const facilityState = data?.facility?.state ?? null;
  const showRegistrationColumn = useShowWasteStateRegistration(facilityState);

  const exportFilename = useMemo(
    () => (data.facility?.name ? `${data.facility.name}-waste-streams` : undefined),
    [data.facility?.name]
  );

  const columns: OmnisearchGridColDef<WasteStreamTileRow>[] = useMemo(() => {
    const base: OmnisearchGridColDef<WasteStreamTileRow>[] = [
      {
        field: "name",
        headerName: "Waste Stream",
        flex: 1,
        minWidth: 100,
        sortable: true,
        headerClassName: "wrapHeader",
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  title={params.value}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {params.value}
                </Typography>
                {params.row.willNotReport && (
                  <Typography variant="caption" color="text.secondary">
                    Not Reportable
                  </Typography>
                )}
              </Box>
              {isStaff && <MergedWasteStreamChip mergeCount={params.row.mergeCount} />}
              <ProvisionalWasteStreamChip isProvisional={params.row.isProvisional} />
            </Box>
            <ArrowRightIcon sx={{ flexShrink: 0, fontSize: 16 }} />
          </Box>
        ),
      },
      {
        field: "profileNumber",
        headerName: "Profile Number",
        flex: 0.5,
        sortable: true,
        valueGetter: profileNumberValueGetter,
      },
      {
        field: "federalWasteCodes",
        headerName: "Federal Waste Code",
        flex: 0.5,
        sortable: true,
        valueGetter(_, row) {
          const federalWasteCodes = row.federalWasteCodes || [];
          return federalWasteCodes.join(", ");
        },
        headerClassName: "wrapHeader",
      },
      {
        field: "stateWasteCodes",
        headerName: "State Waste Code",
        flex: 0.5,
        sortable: true,
        valueGetter(_, row) {
          const stateWasteCodes = row.stateWasteCodes || [];
          return stateWasteCodes.join(", ");
        },
        headerClassName: "wrapHeader",
      },
      {
        field: "classification",
        headerName: "Classification",
        flex: 0.5,
        filterable: false,
        sortable: true,
        valueFormatter: (_, row) => prettyPrintWasteClassification(row.classification),
        headerClassName: "wrapHeader",
      },
      {
        field: "formCode",
        headerName: "Form Code",
        flex: 0.3,
        sortable: true,
        headerClassName: "wrapHeader",
      },
      {
        field: "sourceCode",
        headerName: "Source Code",
        flex: 0.3,
        sortable: true,
        headerClassName: "wrapHeader",
      },
      {
        field: "managementMethodCodes",
        headerName: "Management Method Code",
        flex: 0.5,
        sortable: true,
        valueGetter(_, row) {
          return row.managementMethodCodes.join(", ");
        },
        headerClassName: "wrapHeader",
      },
      {
        field: "vendorAmount",
        headerName: "Amount (Vendor)",
        flex: 0.4,
        filterable: false,
        sortable: true,
        headerClassName: "wrapHeader",
        valueFormatter: formatAmountInPounds,
      },
      {
        field: "eManifestAmount",
        headerName: "Amount (e‑Manifest)",
        flex: 0.4,
        filterable: false,
        sortable: true,
        headerClassName: "wrapHeader",
        valueFormatter: formatAmountInPounds,
      },
      {
        field: "effectiveAmount",
        headerName: "Effective Amount",
        flex: 0.4,
        filterable: false,
        sortable: false,
        headerClassName: "wrapHeader",
        valueFormatter: formatAmountInPounds,
        description:
          "Per manifest line: uses e-Manifest amount if available, otherwise vendor amount",
      },
      {
        field: "willNotReport",
        headerName: "Not Reportable",
        filterKeyType: "boolean",
        sortable: false,
        flex: 0.4,
        headerClassName: "wrapHeader",
        renderCell: ({ value }) => (value === true ? <Check /> : null),
      },
    ];
    if (showRegistrationColumn) {
      base.splice(
        2,
        0,
        {
          field: "stateRegisteredId",
          headerName: "State Registered ID",
          sortable: true,
          filterable: true,
          flex: 0.6,
          headerClassName: "wrapHeader",
          renderCell: ({ value }) => value ?? "",
        },
        {
          field: "stateRegistrationStatus",
          headerName: "Registration Status",
          sortable: true,
          filterable: false,
          flex: 0.5,
          headerClassName: "wrapHeader",
          valueFormatter: (_, row) =>
            prettyPrintWasteStreamRegistrationStatus(row.stateRegistrationStatus),
        }
      );
    }
    return base;
  }, [isStaff, showRegistrationColumn]);

  return (
    <>
      <OmnisearchDataGrid<WasteStreamsQuery, WasteStreamsQueryVariables, WasteStreamTileRow>
        persistenceKey="waste-streams-table"
        isURLDriven={true}
        columns={columns}
        dataQuery={WASTE_STREAMS}
        getItems={(data) => data.facility?.wasteStreams?.items ?? []}
        getCount={(data) => data.facility?.wasteStreams?.count ?? 0}
        dataQueryVariables={{
          facilityId,
          startDate: deferredFilters.startDate,
          endDate: deferredFilters.endDate,
          view: deferredFilters.view,
          tags: deferredFilters.tags,
        }}
        initialPageSize={10}
        withPadding={false}
        defaultSearch=""
        noDataMessage={"No Waste Streams found for this facility."}
        initialSortModel={[{ field: "name", sort: "asc" }]}
        onRowClick={handleRowClick}
        disableRowSelectionOnClick={true}
        onDataStateChange={(data) => {
          setQueryData(data ?? undefined);
        }}
        commandButtons={[
          isStaff ? (
            <Button
              key="merge-waste-streams-button"
              size="small"
              variant="contained"
              sx={{
                color: theme.palette.primary.contrastText,
                backgroundColor: theme.palette.warning.light,
              }}
              endIcon={<ArrowRightIcon />}
              disabled={!canMergeSelected}
              title={`Merge selected waste streams`}
              onClick={() => setIsMergeDialogOpen(true)}
            >
              Merge Waste Streams
            </Button>
          ) : null,
          <WasteStreamExportDropdown
            key="export-waste-streams-button"
            isStaff={isStaff}
            disabled={!facilityId}
            facilityId={facilityId}
            tenantId={tenantId ?? ""}
            fileName={exportFilename}
            filter={{
              search: omnisearch,
              startDate: deferredFilters.startDate,
              endDate: deferredFilters.endDate,
              view: deferredFilters.view,
              tags: deferredFilters.tags,
            }}
            dataQuery={WASTE_STREAMS}
            dataQueryVariables={{
              facilityId,
              startDate: deferredFilters.startDate,
              endDate: deferredFilters.endDate,
              view: deferredFilters.view,
              tags: deferredFilters.tags,
              search: omnisearch || undefined,
            }}
            onWasteStreamClick={pushIdToUrl}
          />,
        ]}
        selectedIds={selectedRows.map((row) => row.id)}
        onSelectedRowsChanged={isStaff ? setSelectedRows : undefined}
        isRowSelectable={isRowSelectable}
        checkboxSelection={isStaff}
        disableSelectAll
        additionalFilterColumns={[
          {
            filterKeyType: "enumAutocomplete",
            key: "classification",
            header: "Classification",
            enumValues: Object.values(WasteClassification),
            enumPresentationFunction: (value) => prettyPrintWasteClassification(value),
          },
          ...(showRegistrationColumn
            ? [
                {
                  filterKeyType: "boolean" as const,
                  key: "needsStateRegistration",
                  header: "Needs State Registration",
                },
                {
                  filterKeyType: "enumAutocomplete" as const,
                  key: "stateRegistrationStatus",
                  header: "Registration Status",
                  enumValues: Object.values(WasteStreamRegistrationStatus),
                  ignoreStateAbbreviations: true,
                },
              ]
            : []),
        ]}
      />

      {isMergeDialogOpen && (
        <WasteStreamMergeDialog
          onClose={() => {
            setIsMergeDialogOpen(false);
            setSelectedRows([]); // Clear selections when dialog closes
          }}
          selectedRows={selectedRows}
        />
      )}

      {selectedId && (
        <WasteStreamDetailsModal
          open={!!selectedId}
          onClose={() => popIdFromUrl()}
          wasteStreamId={selectedId}
          buildLineItemHref={(lineItemId) => `../manifest-line-items/${lineItemId}`}
        />
      )}
    </>
  );
};
